/**
 * FindAgencyPage.tsx — /#/find-agency
 *
 * Step 1 of the multi-tenant login flow.
 * User enters their organization slug → validated against worker →
 * stored in localStorage → redirected to login with tenant context.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'
const WORKER_URL = (import.meta.env.VITE_API_URL || 'https://castellan-security.com').replace(/\/$/, '')

export default function FindAgencyPage() {
  const navigate = useNavigate()
  const [slug, setSlug]       = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')

  async function handleFind(e: React.FormEvent) {
    e.preventDefault()
    const s = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')
    if (!s) { setError('Enter your organization slug'); return }
    setLoading(true); setError('')
    try {
      const res  = await fetch(`${WORKER_URL}/api/v1/tenants/lookup?slug=${encodeURIComponent(s)}`)
      const data = await res.json()
      if (!data.success) { setError(data.error || 'Organization not found. Check the slug and try again.'); return }
      // Redirect to tenant's dedicated subdomain using the subdomain from D1
      const subdomain = data.subdomain || s
      window.location.href = `https://${subdomain}.castellan-security.com`
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: NAVY, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Logo */}
      <div onClick={() => navigate('/')} style={{ fontSize: 28, fontWeight: 800, color: GOLD, letterSpacing: 3, marginBottom: 40, cursor: 'pointer' }}>CASTELLAN</div>

      <div style={{ background: '#0f1a2e', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 420 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', marginBottom: 8, textAlign: 'center' }}>Find your organization</h1>
        <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', marginBottom: 32, lineHeight: 1.6 }}>
          Enter your organization's name or slug to sign in.
        </p>

        <form onSubmit={handleFind}>
          <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
            Organization name or slug
          </label>
          <div style={{ display: 'flex', alignItems: 'center', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
            <span style={{ padding: '10px 10px 10px 14px', color: '#334155', fontSize: 14, userSelect: 'none', whiteSpace: 'nowrap' }}>castellan-security.com/</span>
            <input
              type="text"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="your-org"
              autoFocus
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 14, padding: '10px 14px 10px 0' }}
            />
          </div>
          <p style={{ color: '#334155', fontSize: 12, marginBottom: 24 }}>
            e.g. <span style={{ color: GOLD }}>Fair Oaks Mall Security</span> or <span style={{ color: GOLD }}>fair-oaks-mall</span>
          </p>

          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 16, background: 'rgba(239,68,68,0.08)', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}

          <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px 0', background: loading ? '#64748b' : `linear-gradient(135deg, ${GOLD}, #d97706)`, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Looking up...' : 'Continue →'}
          </button>
        </form>


      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 24 }}>
        {[['Privacy', '/privacy'], ['Terms', '/terms']].map(([l, h]) => (
          <span key={l} onClick={() => navigate(h)} style={{ color: '#334155', cursor: 'pointer', fontSize: 12 }}>{l}</span>
        ))}
      </div>
    </div>
  )
}
